
export type AppRole = 'owner' | 'admin' | 'manager' | 'user';

// Roles that land on the dashboard rather than the board.
const ELEVATED_ROLES: AppRole[] = ['owner', 'admin', 'manager'];

// Where a signed-in user belongs when they hit a public route (/, /auth).
// Shared by Auth and Landing so the two entry points can never disagree about
// where a given role goes.
export const getPostAuthRoute = (roles: (string | null | undefined)[]): string =>
  roles.some((role) => !!role && ELEVATED_ROLES.includes(role as AppRole))
    ? '/dashboard'
    : '/board';

// Define which roles can access which routes
export const routePermissions: Record<string, AppRole[]> = {
  '/dashboard': ['owner', 'admin', 'manager', 'user'],
  '/board': ['owner', 'admin', 'manager', 'user'],
  '/company': ['owner', 'admin'],
  '/employees': ['owner', 'admin', 'manager'],
  '/finances': ['owner', 'admin', 'manager'],
  '/invoices': ['owner', 'admin', 'manager'],
  '/transactions': ['owner', 'admin', 'manager'],
  '/assignments2': ['owner', 'admin', 'manager'],
  '/opportunities': ['owner', 'admin', 'manager'],
  '/inventory': ['owner', 'admin', 'manager'],
  '/settings': ['owner', 'admin'],
  // Include clients and projects
  '/clients': ['owner', 'admin', 'manager'],
  '/projects': ['owner', 'admin', 'manager'],
};

// Helper function to check if a path belongs to a specific base route
export const pathMatchesRoute = (path: string, route: string): boolean => {
  // Exact match
  if (path === route) return true;
  
  // Check for dynamic routes like /clients/:id or /projects/:id
  if (route === '/clients' && path.startsWith('/clients/')) return true;
  if (route === '/projects' && path.startsWith('/projects/')) return true;
  if (route === '/invoices' && path.startsWith('/invoice/')) return true;
  if (route === '/inventory' && path.startsWith('/purchase-requests/')) return true;
  
  return false;
};

// Get the base route for a given path
export const getBaseRouteForPath = (path: string): string => {
  if (path === '/') return path;
  
  // Check if path is a direct match to a defined route
  if (routePermissions[path]) return path;
  
  // Check for dynamic routes
  if (path.startsWith('/clients/')) return '/clients';
  if (path.startsWith('/projects/')) return '/projects';
  if (path.startsWith('/invoice/')) return '/invoices';
  if (path.startsWith('/purchase-requests/')) return '/inventory';
  
  // If no match found, return the path itself
  return path;
};

// Check if a role grants access to a route. Pure/sync on purpose: the role
// comes from UserContext, so navigation never triggers extra network calls.
export const checkRoutePermission = (path: string, role: string | null): boolean => {
  if (!role) return false;

  const baseRoute = getBaseRouteForPath(path);
  const allowedRoles = routePermissions[baseRoute];

  // If no permissions defined for this route, deny by default
  if (!allowedRoles) return false;

  return allowedRoles.includes(role as AppRole);
};
